/**
 * Caixa de aviso — erro, atenção, sucesso e informação.
 *
 * Existe porque as telas de autenticação mostravam erro em texto pequeno
 * vermelho, com o mesmo peso visual do resto do formulário. Quem errava a
 * senha precisava procurar o que tinha mudado na tela.
 *
 * O design system já pedia outra coisa (docs/06_DESIGN_SYSTEM.md §2):
 * **cor, forma e palavra sempre juntas**. Cor sozinha não chega a quem tem
 * daltonismo, não chega a quem está no sol, e não chama atenção de ninguém
 * quando o texto tem o tamanho do texto ao lado.
 *
 * Aqui a caixa traz fundo próprio, ícone e texto — três sinais para a mesma
 * mensagem.
 *
 * **Não é popup, e isso é escolha.** Erro de formulário fica onde está o
 * problema; um aviso que aparece no topo e some faz a pessoa procurar o que
 * fazer. Popup serve para o que acontece em segundo plano — "treino salvo",
 * "sincronizado" —, que não tem lugar fixo na tela e chega no Marco 3.
 *
 * Todas as combinações abaixo foram **medidas**, não estimadas. A primeira
 * tentativa usava `--color-success` sobre `--color-mint-100` e dava 4,28:1,
 * abaixo do mínimo de 4,5:1 da WCAG AA.
 */

import type { ReactNode } from 'react';

export type AlertTone = 'error' | 'warning' | 'success' | 'info';

interface AlertProps {
  tone: AlertTone;
  children: ReactNode;
  /** `assertive` interrompe o leitor de tela. Só para o que impede de seguir. */
  live?: 'polite' | 'assertive';
}

const TONES: Record<AlertTone, { surface: string; ink: string; ratio: string }> = {
  // fundo coral-100, texto danger
  error: { surface: '#FFE7E0', ink: '#B3392F', ratio: '5,01:1' },
  // fundo amber-100, texto warning
  warning: { surface: '#FEF0D4', ink: '#8A5E06', ratio: '5,06:1' },
  // fundo mint-100, texto mint-700 — o `--color-success` reprovava aqui
  success: { surface: '#D3F4EA', ink: '#0C6154', ratio: '6,27:1' },
  // fundo amber-50, texto info
  info: { surface: '#FFF9EE', ink: '#2F6FBF', ratio: '4,83:1' },
};

export function Alert({ tone, children, live = 'polite' }: AlertProps) {
  const { surface, ink } = TONES[tone];

  return (
    <div
      // `role="alert"` já implica `aria-live="assertive"`; para os tons que não
      // impedem de seguir, `status` anuncia sem cortar a leitura em curso.
      role={live === 'assertive' ? 'alert' : 'status'}
      className="flex items-start gap-3 rounded-xl px-4 py-3"
      style={{ backgroundColor: surface, color: ink }}
    >
      <AlertIcon tone={tone} />
      <div className="flex-1 text-sm font-medium leading-snug">{children}</div>
    </div>
  );
}

/**
 * A forma muda com o tom, não só a cor.
 *
 * Triângulo para erro, círculo com traço para atenção, círculo com check para
 * sucesso — os mesmos da tabela de severidade do design system.
 */
function AlertIcon({ tone }: { tone: AlertTone }) {
  const common = {
    width: 20,
    height: 20,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 2,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    'aria-hidden': true,
    focusable: false,
    className: 'mt-0.5 shrink-0',
  };

  if (tone === 'error') {
    return (
      <svg {...common}>
        <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" />
        <path d="M12 9v4" />
        <path d="M12 17h.01" />
      </svg>
    );
  }

  if (tone === 'success') {
    return (
      <svg {...common}>
        <circle cx="12" cy="12" r="9" />
        <path d="m8.5 12.5 2.5 2.5 4.5-5" />
      </svg>
    );
  }

  // atenção e informação compartilham o círculo, e se distinguem pelo traço
  return (
    <svg {...common}>
      <circle cx="12" cy="12" r="9" />
      {tone === 'warning' ? (
        <>
          <path d="M12 8v4" />
          <path d="M12 16h.01" />
        </>
      ) : (
        <>
          <path d="M12 16v-4" />
          <path d="M12 8h.01" />
        </>
      )}
    </svg>
  );
}
